import React, { useRef } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import { Table, Container, Row, Col, Card, Button } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

export default function TableNo11() {
  const pdfRef = useRef();

  const raceData = [
    { race: "American Indian or Alaska Native Alone", share: 1.43 },
    { race: "Asian Alone", share: 5.13 },
    { race: "Black or African American Alone", share: 3.02 },
    { race: "Native Hawaiian or Other Pacific Islander Alone", share: 1.01 },
    { race: "Two or More Race Groups", share: 2.03 },
    { race: "White Alone", share: 87.37 },
  ];

  const topRace = raceData.reduce((max, r) => (r.share > max.share ? r : max));
  const avgShare = (
    raceData.reduce((sum, r) => sum + r.share, 0) / raceData.length
  ).toFixed(2);

  const chartData = {
    labels: raceData.map((r) => r.race),
    datasets: [
      {
        label: "Employment Share (%)",
        data: raceData.map((r) => r.share),
        backgroundColor: [
          "#B45309",
          "#10B981",
          "#374151",
          "#F59E0B",
          "#DFCCF1",
          "#9CA3AF",
        ],
        borderColor: "#fff",
        borderWidth: 2,
        hoverOffset: 15,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: true,
        text: "📊 Employment Share by Race (2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.parsed.toFixed(2)}%`,
        },
      },
    },
  };

  // PDF Download
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text("20250916.v3.198", 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );
        pdf.save("employment_share_by_race.pdf");
        // input.style.width = originalWidth;
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-green-50 via-white to-emerald-50 rounded-xl shadow-lg"
      >
        {/* Title */}
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Share by Race (2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Share</p>
            <p className="text-lg font-bold text-green-800">
              {topRace.race} ({topRace.share.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Average Representation
            </p>
            <p className="text-lg font-bold text-yellow-800">{avgShare}%</p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Groups Analyzed
            </p>
            <p className="text-lg font-bold text-blue-800">{raceData.length}</p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"> */}
        {/* Table + Chart */}
        <Row className="gy-4 flex items-center">
          {/* Table */}
          <Col md={6}>
            <div>
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-emerald-700 text-white">
                  <tr>
                    <th className="p-2">Race</th>
                    <th className="p-2">Employment Share (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {raceData.map((r) => (
                    <tr
                      key={r.race}
                      className={`${
                        r.race === topRace.race
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2">{r.race}</td>
                      <td className="p-2">{r.share.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Col>

          {/* Chart */}
          <Col md={6}>
            <div className="p-3 bg-white rounded-3 shadow-sm">
              <div
                style={{
                  height: "400px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Pie data={chartData} options={options} />
              </div>
            </div>
          </Col>
        </Row>
        {/* </div> */}

        {/* Summary */}
        <p className="text-center text-gray-600 mt-3">
          📈{" "}
          <span className="font-semibold text-green-700">{topRace.race}</span>{" "}
          represents the largest portion of Utah’s workforce at{" "}
          <span className="font-semibold">{topRace.share.toFixed(2)}%</span>,
          while all other groups combined make up the remaining{" "}
          <span className="font-semibold">
            {(100 - topRace.share).toFixed(2)}%
          </span>
          .
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
